import { useTranslation } from "react-i18next";

import { l } from "@aviary/locales/i18n";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { catalogTypeVar } from "@shared/reactiveVar/useCatalogType/useCatalogType.store";
import { AvailabilityStatus } from "@shared/types/availabilityStatus";
import { getExpectedReturnDate } from "@shared/utils/getExpectedReturnDate/getExpectedReturnDate";

import type { AvailabilityPillStatus } from "../molecules/ProductImage/AvailabilityPill";

const useProductAvailabilityLabel = (
  availabilityStatus: AvailabilityPillStatus,
  expectedReturnDate?: string
) => {
  const { t } = useTranslation();
  const { isPatient } = useSharedGlobalConfig();
  const { isValid, returnDate, daysToReturn } = getExpectedReturnDate(expectedReturnDate);
  const isDTP = catalogTypeVar().type === "DTP";

  let availabilityLabelText = null;
  let expectedReturnDateText = null;

  const backorderedWithNoReturnDate = isDTP
    ? t(l.aviary.productImage.backorderedWithNoReturnDate)
    : t(l.aviary.productImage.wholesaleBackorderedWithNoReturnDate);

  if (availabilityStatus === AvailabilityStatus.BACKORDERED) {
    availabilityLabelText = t(l.aviary.productImage.backordered);
    expectedReturnDateText = isValid
      ? t(l.aviary.productImage.backorderedWithReturnDate, { returnDate, count: daysToReturn })
      : backorderedWithNoReturnDate;
  }

  if (availabilityStatus === AvailabilityStatus.DISCONTINUED) {
    availabilityLabelText = t(l.aviary.productImage.discontinued);
    expectedReturnDateText = isPatient
      ? t(l.aviary.productImage.discontinuedNoReturnDateForPatient)
      : t(l.aviary.productImage.discontinuedNoReturnDate);
  }

  if (availabilityStatus === AvailabilityStatus.TO_BE_DISCONTINUED) {
    availabilityLabelText = t(l.aviary.productImage.toBeDiscontinued);
    // TODO: This message will be changed once Thomas is back from his vacation
    expectedReturnDateText = t(l.aviary.productImage.toBeDiscontinuedNoReturnDate);
  }

  if (
    availabilityStatus === AvailabilityStatus.UNAVAILABLE ||
    availabilityStatus === AvailabilityStatus.NA
  ) {
    availabilityLabelText = t(l.aviary.productImage.outOfStock);
    expectedReturnDateText = isValid
      ? t(l.aviary.productImage.outOfStockWithReturnDate, { returnDate, count: daysToReturn })
      : t(l.aviary.productImage.outOfStockWithNoReturnDate);
  }

  if (availabilityStatus === AvailabilityStatus.FULLY_RESTRICTED) {
    availabilityLabelText = t(l.aviary.productImage.restricted);
    expectedReturnDateText = t(l.aviary.productImage.restrictedDescription);
  }

  return { availabilityLabelText, expectedReturnDateText };
};

export { useProductAvailabilityLabel };
